import { isEmpty } from 'lodash'
import { useQueryFilterStore } from '../query-filter-modal/query-filter-store'
import AdvanceFilterItem from './advance-filter-item'
import { Button } from '../ui/button'
import { ChevronRightIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useMutation } from '@tanstack/react-query'
import { filterCriteriaFilterCriteriaPostMutation } from '@/generated/@tanstack/react-query.gen'
import { toast } from 'react-toastify'

interface Props {
  onClear: () => void
  isOpenFilterCriteria: boolean
  onSubmitDone: (data: any) => void
}

export default function AdvanceFilters({
  onClear,
  isOpenFilterCriteria,
  onSubmitDone,
}: Props) {
  const { formData, resetValue, resetForm, updateValue } = useQueryFilterStore()
  const {
    counties,
    owners,
    gsas,
    water_districts,
    main_crops,
    max_hs,
    min_hs,
    max_acreage,
    min_acreage,
    max_ss,
    min_ss,
  } = formData
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const { mutate } = useMutation({
    ...filterCriteriaFilterCriteriaPostMutation({}),
    onError: (error) => {
      toast.error(error.toString())
    },

    onSuccess: (res: any) => {
      toast.success(
        `${res.result.features.length} parcel(s) returned from the search. (Parcels without a Hydra score are not included in the results)`
      )
      onSubmitDone(res.result)
    },
  })

  useEffect(() => {
    if (isOpenFilterCriteria) return
    if (isEmpty(formData.counties)) return
    mutate({
      body: {
        ...formData,
        max_acreage: formData.max_acreage
          ? formData.max_acreage.toString()
          : '',
        min_acreage: formData.min_acreage
          ? formData.min_acreage.toString()
          : '',
        session_key: JSON.parse(localStorage.auth).sessionKey,
      },
    })
  }, [formData])

  const handleScroll = () => {
    const container = scrollContainerRef.current
    if (container) {
      const newScrollPosition = container.scrollLeft + 200
      container.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth',
      })
    }
  }

  const checkScroll = () => {
    const container = scrollContainerRef.current
    if (container) {
      const hasReachedEnd =
        container.scrollLeft + container.clientWidth >= container.scrollWidth
      setCanScrollRight(!hasReachedEnd)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScroll)
      checkScroll()
      return () => container.removeEventListener('scroll', checkScroll)
    }
  }, [formData])

  if (isEmpty(counties)) return <></>

  return (
    <div
      className="
    hidden md:flex
    absolute top-[56px] right-[10px] left-[50px] 
    2xl:left-[376px]
    2xl:top-[10px]"
    >
      <div className="w-fit h-fit flex-shrink">
        <div className="flex flex-row h-full flex-shrink">
          <div
            ref={scrollContainerRef}
            className={cn(
              'flex items-center h-full rounded-l-sm bg-white text-sm overflow-x-auto w-fit gap-2 px-2',
              '[&::-webkit-scrollbar]:hidden scrollbar-none min-h-[36px]',
              'max-w-[100px] md:max-w-[100px] lg:max-w-[380px] xl:max-w-[600px] 2xl:max-w-[550px]'
            )}
          >
            {counties.map((item) => (
              <AdvanceFilterItem
                title="County:"
                value={[item]}
                onRemove={() => {
                  const newValue = counties.filter((c) => c !== item)
                  updateValue('counties', newValue)
                  if (newValue.length === 0) {
                    resetForm()
                  }
                  onClear()
                }}
              />
            ))}
            {owners.map((item) => (
              <AdvanceFilterItem
                title="Owner:"
                value={[item]}
                onRemove={() => resetValue('owners')}
              />
            ))}

            {gsas.map((item) => (
              <AdvanceFilterItem
                title="GSA:"
                value={[item]}
                onRemove={() => resetValue('gsas')}
              />
            ))}

            {water_districts.map((item) => (
              <AdvanceFilterItem
                title="Water District:"
                value={[item]}
                onRemove={() => resetValue('water_districts')}
              />
            ))}

            {main_crops.map((item) => (
              <AdvanceFilterItem
                title="Main Crop:"
                value={[item]}
                onRemove={() => resetValue('main_crops')}
              />
            ))}

            <AdvanceFilterItem
              title="Hydra Score:"
              value={{
                min: min_hs,
                max: max_hs,
              }}
              onRemove={() => {
                resetValue('max_hs')
                resetValue('min_hs')
              }}
            />
            <AdvanceFilterItem
              title="Soil Score:"
              value={{
                min: min_ss,
                max: max_ss,
              }}
              onRemove={() => {
                resetValue('max_ss')
                resetValue('min_ss')
              }}
            />
            <AdvanceFilterItem
              title="Acreage:"
              value={{
                min: min_acreage ?? 0,
                max: max_acreage ?? 0,
              }}
              onRemove={() => {
                resetValue('max_acreage')
                resetValue('min_acreage')
              }}
            />
          </div>
          <div className="pr-2 bg-white flex flex-row items-center rounded-r-sm border-l h-full min-h-[36px]">
            {canScrollRight && (
              <div
                onClick={handleScroll}
                className={cn(
                  'border-l h-full items-center flex justify-center w-9',
                  canScrollRight
                    ? 'cursor-pointer hover:bg-gray-50'
                    : 'opacity-50 cursor-not-allowed'
                )}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </div>
            )}
            <Button
              onClick={() => {
                resetForm()
                onClear()
              }}
              className="max-h-6 opacity-90 border-[#D5D5D5] border rounded-lg ml-2"
              variant={'window'}
            >
              <p className="font-bold text-xs text-[#20222499]">
                Clear Filters
              </p>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
